/**
 * Turn a block's manifest into an editable PROPERTY FORM (R2 builder).
 *
 * The editor showed a block's props as raw JSON — powerful, and useless to a non-engineer. The
 * manifest already declares every prop's type, whether it is required, its default, and its enum
 * values, so a typed form can be derived from it: a checkbox for a boolean, a select for an enum,
 * a number field for a number. That declaration is the one source of truth; this reads it rather
 * than hard-coding a form per block.
 *
 * Pure and testable, like the structural ops — the field derivation and the value coercion are
 * where the subtle bugs live (a number field hands back a string; an empty field is not zero), so
 * they belong where a Node suite can pin them.
 */
import type { BlockManifest } from './template-schema.js';

/** How the builder should render a field. `json` is the escape hatch for a non-scalar value. */
export type PropFieldType = 'string' | 'number' | 'boolean' | 'enum' | 'color' | 'asset' | 'json';

export interface PropField {
  name: string;
  type: PropFieldType;
  required: boolean;
  /** Present for `enum`. */
  values?: string[];
  description: string;
  /** Current value: the set value, else the manifest default, else undefined. */
  value: unknown;
  /** False for a prop present on the block but NOT declared by its manifest. */
  declared: boolean;
}

function inferType(value: unknown): PropFieldType {
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (value !== null && typeof value === 'object') return 'json';
  return 'string';
}

/**
 * The fields to render for a block, in a stable order.
 *
 * Declared props first, in manifest order, so the form reads the way the block's author
 * described it. Then any UNDECLARED props the block happens to carry — surfaced rather than
 * hidden, because a prop that edits nothing (a typo, a removed feature) should be visible and
 * removable, not silently preserved forever. A non-scalar value falls to the `json` escape
 * hatch so it is still editable.
 */
export function propFields(
  manifest: BlockManifest | undefined,
  props: Record<string, unknown> | undefined,
): PropField[] {
  const p = props ?? {};
  const fields: PropField[] = [];
  const declared = new Set<string>();

  for (const [name, spec] of Object.entries(manifest?.props ?? {})) {
    declared.add(name);
    const has = Object.prototype.hasOwnProperty.call(p, name);
    fields.push({
      name,
      type: spec.type as PropFieldType,
      required: spec.required ?? false,
      values: spec.values,
      description: spec.description ?? '',
      value: has ? p[name] : spec.default,
      declared: true,
    });
  }

  for (const [name, value] of Object.entries(p)) {
    if (declared.has(name)) continue;
    fields.push({
      name, type: inferType(value), required: false,
      description: 'Not declared by this block — editing it does nothing until the block supports it.',
      value, declared: false,
    });
  }

  return fields;
}

/**
 * Coerce a form input's raw value to the prop's real type.
 *
 * A number field yields a STRING; storing it unturned would make `columns: "4"` where the block
 * expects `4`, and the failure shows up as a mis-rendered report rather than an error. An empty
 * number field is `undefined`, not `0` — a blank optional number means "unset", and zero is a
 * value someone might have meant. A malformed JSON field throws, so the caller can refuse the
 * edit rather than store a corrupt prop.
 */
export function coerceProp(type: PropFieldType, raw: string | boolean): unknown {
  if (type === 'boolean') return Boolean(raw);
  if (type === 'number') {
    if (raw === '' || raw === false) return undefined;
    const n = Number(raw);
    if (Number.isNaN(n)) throw new Error(`"${String(raw)}" is not a number`);
    return n;
  }
  if (type === 'json') {
    const s = String(raw).trim();
    if (s === '') return undefined;
    return JSON.parse(s); // throws on malformed — the caller surfaces it
  }
  // string | enum | color | asset all round-trip as text.
  return String(raw);
}
